import { Booking, Camp } from '../types';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generateBookingPDF = (booking: Booking, camp: Camp): jsPDF => {
  const doc = new jsPDF();

  // Title
  doc.setFontSize(22);
  doc.setTextColor(26, 95, 122); // Using the brand teal color
  doc.text('Southern Comfort Camps', 105, 20, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(87, 101, 116);
  doc.text('Booking Request Acknowledgement', 105, 28, { align: 'center' });

  // Status Notice Badge
  doc.setFillColor(245, 248, 250);
  doc.rect(20, 35, 170, 12, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 95, 122);
  doc.text('STATUS: PENDING HOST CONFIRMATION', 105, 42, { align: 'center' });

  // Reset Text Color for details
  doc.setTextColor(44, 62, 80);
  doc.setFontSize(11);
  let yPos = 60;

  // Booking Details Section Title
  doc.setFont('helvetica', 'bold');
  doc.text('BOOKING DETAILS', 20, yPos);
  doc.line(20, yPos + 2, 190, yPos + 2);
  yPos += 10;

  doc.text('Request ID:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.id, 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Camp:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(camp.name, 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Location:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(camp.location, 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Check-in:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.checkIn.toLocaleDateString(), 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Check-out:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.checkOut.toLocaleDateString(), 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Number of Nights:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  const nights = Math.ceil((booking.checkOut.getTime() - booking.checkIn.getTime()) / (1000 * 60 * 60 * 24));
  doc.text(nights.toString(), 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Guests:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.guests.toString(), 70, yPos);

  // Guest Information Section
  yPos += 14;
  doc.setFont('helvetica', 'bold');
  doc.text('GUEST INFORMATION', 20, yPos);
  doc.line(20, yPos + 2, 190, yPos + 2);
  yPos += 10;

  doc.text('Name:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.guestName, 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Email:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.guestEmail, 70, yPos);

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Phone:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(booking.guestPhone, 70, yPos);

  if (booking.specialRequests) {
    yPos += 14;
    doc.setFont('helvetica', 'bold');
    doc.text('Special Requests:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    const splitText = doc.splitTextToSize(booking.specialRequests, 170);
    doc.text(splitText, 20, yPos + 8);
    yPos += 8 + (splitText.length * 5);
  }

  // Pricing Section Title
  yPos += 14;
  doc.setFont('helvetica', 'bold');
  doc.text('ESTIMATED TARIFF', 20, yPos);
  doc.line(20, yPos + 2, 190, yPos + 2);

  // Pricing Table (No payment collection yet)
  yPos += 6;
  autoTable(doc, {
    startY: yPos,
    head: [['Description', 'Estimated Amount']],
    body: [
      [`$${camp.pricePerNight} x ${nights} nights`, `$${(camp.pricePerNight * nights).toFixed(2)}`],
      ['Total Estimated', `$${booking.totalPrice.toFixed(2)}`],
    ],
    theme: 'striped',
    headStyles: { fillColor: [26, 95, 122] },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 50, halign: 'right' },
    },
  });

  // Footer Notes
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 95, 122);
  doc.text('Next Steps:', 20, pageHeight - 35);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(87, 101, 116);
  doc.text('1. The SoCo team will review your dates and availability.', 20, pageHeight - 29);
  doc.text('2. Once approved, you will receive a confirmation email with payment details.', 20, pageHeight - 23);
  doc.text('3. Your booking is only secured after your deposit has been received.', 20, pageHeight - 17);

  // Centered branding footer
  doc.setFontSize(8.5);
  doc.setTextColor(140, 150, 160);
  doc.text('Southern Comfort Camps - Abrolhos Islands', 105, pageHeight - 8, { align: 'center' });

  return doc;
};

export interface ConfirmationPdfInput {
  bookingRef: string;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  campName: string;
  location?: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: number;
  total: number;
  depositPercent: number;
  depositAmount: number;
  balanceAmount: number;
  balanceDueDate: Date;
  bankAccountName: string;
  bankBsb: string;
  bankAccountNumber: string;
  payId?: string;
  paymentReference: string;
  /** Optional per-camp cost breakdown (used for whole-island / both-camps bookings). If given, these rows replace the single "Total stay" row, followed by a "Total stay" summary row. */
  costLines?: { label: string; amount: number }[];
}

/** Professional booking-confirmation PDF to send to guests (bank transfer / PayID payment). */
export const generateConfirmationPDF = (b: ConfirmationPdfInput): jsPDF => {
  const doc = new jsPDF();
  const BRAND: [number, number, number] = [26, 95, 122];
  const MUTED: [number, number, number] = [87, 101, 116];
  const TEXT: [number, number, number] = [44, 62, 80];
  const fmt = (n: number) => `$${n.toFixed(2)} AUD`;
  const fmtDate = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

  // Page geometry - used to stop content running into the footer or off
  // the bottom of the page. If a section won't fit in the remaining space,
  // we start a fresh page for it rather than letting it overlap the footer.
  const pageHeight = doc.internal.pageSize.height;
  const TOP_MARGIN = 20;
  const FOOTER_RESERVE = 24; // keep content clear of the footer line at pageHeight - 10
  const ensureSpace = (neededHeight: number) => {
    if (y + neededHeight > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      y = TOP_MARGIN;
    }
  };

  // Header
  doc.setFontSize(22);
  doc.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
  doc.text('Southern Comfort Camps', 105, 20, { align: 'center' });
  doc.setFontSize(14);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text('Booking Confirmation', 105, 28, { align: 'center' });

  const labelValue = (label: string, value: string, yy: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    doc.setFontSize(10.5);
    doc.text(label, 20, yy);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 72, yy);
  };
  const sectionTitle = (title: string, yy: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
    doc.text(title, 20, yy);
    doc.setDrawColor(BRAND[0], BRAND[1], BRAND[2]);
    doc.line(20, yy + 2, 190, yy + 2);
  };

  // Condensed table styling shared by both tables below - smaller padding
  // keeps the whole document tighter so the payment details usually still
  // land on page one.
  const tableStyles = { fontSize: 9.5, cellPadding: 2.5 };

  let y = 44;
  sectionTitle('BOOKING SUMMARY', y);
  y += 7;
  labelValue('Reference:', b.bookingRef, y); y += 6.5;
  labelValue('Camp:', b.location ? `${b.campName} (${b.location})` : b.campName, y); y += 6.5;
  labelValue('Check-in:', fmtDate(b.checkIn), y); y += 6.5;
  labelValue('Check-out:', fmtDate(b.checkOut), y); y += 6.5;
  labelValue('Nights:', String(b.nights), y); y += 6.5;
  labelValue('Guests:', String(b.guests), y); y += 6.5;
  labelValue('Guest:', b.guestName, y); y += 6.5;
  labelValue('Email:', b.guestEmail, y); y += 6.5;
  if (b.guestPhone) { labelValue('Phone:', b.guestPhone, y); y += 6.5; }

  // Cost of the stay (informational - what the total is made up of)
  y += 5;
  const costRowCount = (b.costLines && b.costLines.length > 0 ? b.costLines.length : 1) + 1;
  ensureSpace(3 + 7 + costRowCount * 7 + 5);
  sectionTitle('COST OF STAY', y);
  y += 3;
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ...(b.costLines && b.costLines.length > 0
        ? b.costLines.map((l) => [l.label, fmt(l.amount)])
        : [[`$${(b.total / b.nights).toFixed(2)} x ${b.nights} nights`, fmt(b.total)]]),
      ['Total stay', fmt(b.total)],
    ],
    theme: 'striped',
    headStyles: { fillColor: BRAND },
    styles: tableStyles,
    columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 40, halign: 'right' } },
    margin: { left: 20, right: 20 },
    didParseCell: (data) => {
      // Bold the last row ("Total stay") so it reads as a subtotal, not another line item.
      if (data.section === 'body' && data.row.index === data.table.body.length - 1) {
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });
  const afterCostTable = doc as unknown as { lastAutoTable?: { finalY?: number } };
  y = (afterCostTable.lastAutoTable?.finalY ?? y + 24) + 8;

  // Deposit & balance - kept in its own table, clearly split from the cost
  // of the stay above, so it reads as "how the total is paid" rather than
  // additional charges on top of the total.
  ensureSpace(6 + 4 + 7 + 2 * 7 + 5);
  sectionTitle('DEPOSIT & BALANCE DUE', y);
  y += 6;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text('These two payments add up to the total stay cost above - not extra charges.', 20, y);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [['Payment', 'Amount', 'Due']],
    body: [
      [`Deposit (${b.depositPercent}%)`, fmt(b.depositAmount), 'To confirm booking'],
      ['Balance', fmt(b.balanceAmount), fmtDate(b.balanceDueDate)],
    ],
    theme: 'striped',
    headStyles: { fillColor: [46, 125, 50] },
    styles: tableStyles,
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: 'right' },
      2: { cellWidth: 50, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  const afterDepositTable = doc as unknown as { lastAutoTable?: { finalY?: number } };
  y = (afterDepositTable.lastAutoTable?.finalY ?? y + 20) + 8;

  // Payment details
  const paymentLineCount = 4 + (b.payId ? 1 : 0);
  ensureSpace(7 + paymentLineCount * 6.5 + 7 + 5);
  sectionTitle('PAYMENT - BANK TRANSFER / PAYID', y);
  y += 7;
  labelValue('Account name:', b.bankAccountName, y); y += 6.5;
  labelValue('BSB:', b.bankBsb, y); y += 6.5;
  labelValue('Account no:', b.bankAccountNumber, y); y += 6.5;
  if (b.payId) { labelValue('PayID:', b.payId, y); y += 6.5; }
  labelValue('Reference:', b.paymentReference, y); y += 6.5;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text('Please use the reference above so we can match your payment.', 20, y);
  y += 7;

  // Policy summary
  // Write the note as one block of text; jsPDF wraps it to the page width (170mm).
  // Blank lines (\n\n) become paragraph breaks. Edit the wording freely below.
  const policyText = `Weather & Safety Cancellations:
Ocean crossings are entirely dependent on conditions. The final decision on whether a crossing can safely go ahead rests with us, though we will always discuss the forecast with you leading up to your trip. If we determine that conditions are unsafe to make the run, you will receive a full refund (including your deposit) or the option to shift your booking to new dates.

Standard Cancellations:
Deposits are non-refundable. For cancellations made 7 or more days prior to your scheduled arrival, any balance paid beyond the depositwill be refunded in full. For cancellations within 7 days of arrival, the balance is non-refundable.

Full terms: see the Terms & Conditions page on our website.`;
  // Computed against page width up front (doesn't depend on y) so we know
  // exactly how tall the block will be before reserving space for it.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const policyLines = doc.splitTextToSize(policyText, 170);
  ensureSpace(7 + policyLines.length * 4.3 + 5);
  sectionTitle('WEATHER AND CANCELLATION POLICY', y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
  doc.text(policyLines, 20, y);
  y += policyLines.length * 4.3;

  // Footer - printed on every page (content may have spilled onto a second
  // page above), always pinned to the bottom margin so it never collides
  // with the content above it.
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text('info@southerncomfortcamps.com', 20, pageHeight - 10);
    doc.text('Southern Comfort Camps - Abrolhos Islands', 190, pageHeight - 10, { align: 'right' });
  }

  return doc;
};

export interface ApprovedBookingPdfInput {
  bookingId: string;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  campName: string;
  checkIn: Date;
  checkOut: Date;
  guests: number;
  totalPrice: number;
  paymentPageUrl: string;
}

export const generateApprovedBookingPDF = (b: ApprovedBookingPdfInput): jsPDF => {
  const doc = new jsPDF();

  doc.setFontSize(22);
  doc.setTextColor(26, 95, 122);
  doc.text('Southern Comfort Camps', 105, 20, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(87, 101, 116);
  doc.text('Booking Confirmed - Payment Instructions', 105, 28, { align: 'center' });

  doc.setFillColor(229, 245, 234);
  doc.rect(20, 34, 170, 12, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 95, 122);
  doc.text('STATUS: HOST APPROVED - COMPLETE PAYMENT TO SECURE YOUR STAY', 105, 41, { align: 'center' });

  doc.setTextColor(44, 62, 80);
  doc.setFontSize(11);
  let y = 54;

  doc.setFont('helvetica', 'bold');
  doc.text('BOOKING', 20, y);
  doc.line(20, y + 2, 190, y + 2);
  y += 10;
  doc.text('Booking ID:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.bookingId, 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Camp:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.campName, 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Check-in:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.checkIn.toLocaleDateString(), 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Check-out:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.checkOut.toLocaleDateString(), 70, y);
  y += 8;
  const nights = Math.ceil((b.checkOut.getTime() - b.checkIn.getTime()) / (1000 * 60 * 60 * 24));
  doc.setFont('helvetica', 'bold');
  doc.text('Nights:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(nights.toString(), 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Guests:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.guests.toString(), 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Total:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`$${b.totalPrice.toFixed(2)} AUD`, 70, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.text('GUEST', 20, y);
  doc.line(20, y + 2, 190, y + 2);
  y += 10;
  doc.text('Name:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.guestName, 70, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Email:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(b.guestEmail, 70, y);
  if (b.guestPhone) {
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Phone:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(b.guestPhone, 70, y);
  }

  y += 22;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 95, 122);
  doc.text('PAY ONLINE', 20, y);
  doc.line(20, y + 2, 190, y + 2);
  y += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(44, 62, 80);
  const payLines = doc.splitTextToSize(
    'Use our secure Stripe Checkout page (test mode applies when the site runs in Stripe test keys):',
    170
  );
  doc.text(payLines, 20, y);
  y += payLines.length * 5 + 4;

  doc.setTextColor(0, 102, 153);
  doc.textWithLink('Open payment page (click or tap)', 20, y, { url: b.paymentPageUrl });
  y += 8;

  doc.setFontSize(9);
  doc.setTextColor(100, 110, 120);
  const urlWrapped = doc.splitTextToSize(b.paymentPageUrl, 170);
  doc.text(urlWrapped, 20, y);

  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(9.5);
  doc.setTextColor(87, 101, 116);
  doc.text('Southern Comfort Camps - Abrolhos Islands', 105, pageHeight - 10, { align: 'center' });

  return doc;
};
